import * as React from 'react';
import { ISimpleDecoratedProps } from './Decorators';
import { IReactChildrenProps } from './Props';

interface IAsAsyncState<TProps> {
  component?: React.ComponentType<TProps> | undefined;
  error?: Error;
}

/**
 * If possible, use a WeakMap to maintain a cache of loaded components.
 * This can be used to synchronously render components that have already been loaded,
 * rather than having to wait for at least one async tick.
 */
const syncModuleCache =
  typeof WeakMap !== 'undefined'
    ? new WeakMap<() => Promise<any>, React.ComponentType<any>>() // tslint:disable-line:no-any
    : undefined;

/**
 * Additional props added to the wrapped component.
 * @public
 */
export interface IAsAsyncProps {
  /**
   * Alternate content to render while the intended component is loading.
   */
  placeholder?: React.ReactNode;
  /**
   * A promise to wait on before loading the intended component.
   */
  barrier?: Promise<void>;
}

/**
 * Additional options to configure the async behavior.
 * @public
 */
export interface IAsAsyncOptions {
  /**
   * Whether or not to render the provided children until the component is loaded.
   */
  useChildrenAsPlaceholder?: boolean;
}

/**
 * Represents the surface of a module which has a default export.
 * @public
 */
export interface IModuleWithDefault<T> {
  default: T;
}

/**
 * Produces a component which internally loads the target component before first mount.
 * The component passes all props through to the loaded component.
 *
 * This overload accepts a module with a default export for the component.
 * @public
 */
export function asAsync<TProps>(
  load: () => Promise<IModuleWithDefault<React.ComponentType<TProps>>>,
  options?: IAsAsyncOptions
): React.ComponentType<ISimpleDecoratedProps<TProps, IAsAsyncProps>>;
/**
 * Produces a component which internally loads the target component before first mount.
 * The component passes all props through to the loaded component.
 *
 * This overload accepts a component directly.
 * @public
 */
export function asAsync<TProps>(
  load: () => Promise<React.ComponentType<TProps>>,
  options?: IAsAsyncOptions
): React.ComponentType<ISimpleDecoratedProps<TProps, IAsAsyncProps>>;
/**
 * Implementation of asAsync.
 * @public
 */
export function asAsync<TProps>(
  load: () => Promise<React.ComponentType<TProps> | IModuleWithDefault<React.ComponentType<TProps>>>,
  options: IAsAsyncOptions = {}
): React.ComponentType<ISimpleDecoratedProps<TProps, IAsAsyncProps>> {
  const {
    useChildrenAsPlaceholder = false
  } = options;

  class Async extends React.PureComponent<ISimpleDecoratedProps<TProps, IAsAsyncProps>, IAsAsyncState<TProps>> {
    private _isDisposed?: boolean;

    constructor(props: TProps) {
      super(props);

      this.state = {
        ...(syncModuleCache && syncModuleCache.has(load)
          ? {
            component: syncModuleCache.get(load)
          }
          : {})
      };
    }

    public async componentDidMount(): Promise<void> {
      if (this.state.component) {
        return;
      }

      const {
        barrier
      } = this.props;

      // Resolve the component during this lifecycle method so
      // if the promise resolves synchronously, setState does not trigger another update.
      try {
        if (barrier) {
          await barrier;
        }

        const component = getModuleDefaultOrModule(await load());

        if (syncModuleCache) {
          syncModuleCache.set(load, component);
        }

        if (this._isDisposed) {
          return;
        }

        this.setState({
          component: component
        });
      } catch (e) {
        if (this._isDisposed) {
          return;
        }

        this.setState({
          error: e instanceof Error ? e : new Error(`${e}`)
        });
      }
    }

    public render(): JSX.Element | null {
      const {
        component: Component,
        error
      } = this.state;

      const {
        children,
        placeholder,
        barrier, // Unused.
        ...props
      } = this.props as IAsAsyncProps & IReactChildrenProps;

      if (error) {
        // Async load errors are significant, so expect the host to use an error boundary to catch and handle them.
        throw error;
      }

      return Component ? (
        <Component { ...props as TProps } children={ children } />
      ) : useChildrenAsPlaceholder ? (
        <>
          { children }
        </>
      ) : placeholder ? (
        <>
          { placeholder }
        </>
      ) : null;
    }

    public componentWillUnmount(): void {
      this._isDisposed = true;
    }
  }

  return Async;
}

/**
 * For a given type, gets the default export from a module, or gets the whole module if there is no
 * default export.
 * @public
 */
export function getModuleDefaultOrModule<T extends object>(valueOrModuleWithDefault: T | IModuleWithDefault<T>): T {
  return (valueOrModuleWithDefault as IModuleWithDefault<T>).default || valueOrModuleWithDefault;
}
